import React, { useState, useEffect } from 'react';
import { Grid, Typography, Chip, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import withStyles from '@mui/styles/withStyles';
import { useSelector } from 'react-redux';
import HTTP from 'services/http';
import { Loader } from 'components/loading-overlay/loading-overlay';
import { IState } from 'interfaces/redux/IState';
import { styles } from './overdue-fcs-styles';

interface IOverdueFCsData {
  customer_name: string;
  overdue_0_days: number;
  overdue_1_day: number;
  overdue_1_3_days: number;
  overdue_4_6_days: number;
  overdue_7_10_days: number;
  overdue_11_plus_days: number;
}

interface IOverdueFCsTabProps {
  classes: Record<string, string>;
}

interface ICustomerOption {
  id: string;
  label: string;
}

const OverdueFCsTab: React.FC<IOverdueFCsTabProps> = ({ classes }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IOverdueFCsData[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<ICustomerOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<ICustomerOption[]>([]);

  const filters = useSelector((state: IState) => state.filters);

  useEffect(() => {
    if (filters?.customers) {
      const options = filters.customers.map((c: any) => ({
        id: String(c.id || c.value),
        label: c.short_name || c.name || c.label,
      }));
      setCustomerOptions(options);
    }
  }, [filters]);

  const fetchOverdueFCsData = async () => {
    setLoading(true);
    try {
      const customerIds =
        selectedCustomers.length > 0 ? selectedCustomers.map(c => c.id).join(',') : undefined;

      const response = await HTTP.get('/reports/overdue-fcs', {
        params: {
          customer_id: customerIds,
        },
      });
      const overdueFcsData = response.data?.overdue_fcs || response.data || [];
      setData(Array.isArray(overdueFcsData) ? overdueFcsData : []);
    } catch (error) {
      // Error fetching overdue FCs data
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverdueFCsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  const baseCustomers = selectedCustomers.length > 0 ? selectedCustomers : customerOptions;
  const baseCustomerNames = new Set(baseCustomers.map(customer => customer.label));
  const overdueByCustomer = new Map(
    data.map(row => [row.customer_name, row] as [string, IOverdueFCsData]),
  );
  const baseRows = baseCustomers.map(customer => ({
    customer_name: customer.label,
    overdue_0_days: overdueByCustomer.get(customer.label)?.overdue_0_days || 0,
    overdue_1_day: overdueByCustomer.get(customer.label)?.overdue_1_day || 0,
    overdue_1_3_days: overdueByCustomer.get(customer.label)?.overdue_1_3_days || 0,
    overdue_4_6_days: overdueByCustomer.get(customer.label)?.overdue_4_6_days || 0,
    overdue_7_10_days: overdueByCustomer.get(customer.label)?.overdue_7_10_days || 0,
    overdue_11_plus_days: overdueByCustomer.get(customer.label)?.overdue_11_plus_days || 0,
  }));
  const additionalRows = data.filter(row => !baseCustomerNames.has(row.customer_name));
  const displayData = [...baseRows, ...additionalRows];

  const calculateTotals = (rows: IOverdueFCsData[]) => {
    return rows.reduce(
      (acc, row) => ({
        overdue_0_days: acc.overdue_0_days + row.overdue_0_days,
        overdue_1_day: acc.overdue_1_day + row.overdue_1_day,
        overdue_1_3_days: acc.overdue_1_3_days + row.overdue_1_3_days,
        overdue_4_6_days: acc.overdue_4_6_days + row.overdue_4_6_days,
        overdue_7_10_days: acc.overdue_7_10_days + row.overdue_7_10_days,
        overdue_11_plus_days: acc.overdue_11_plus_days + row.overdue_11_plus_days,
      }),
      {
        overdue_0_days: 0,
        overdue_1_day: 0,
        overdue_1_3_days: 0,
        overdue_4_6_days: 0,
        overdue_7_10_days: 0,
        overdue_11_plus_days: 0,
      },
    );
  };

  const totals = calculateTotals(displayData);

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.title}>
            Current Overdue FCs
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container direction="column">
            <Grid item>
              <span className={classes.filterLabel}>Customer</span>
            </Grid>
            <Grid item>
              <Autocomplete
                multiple
                filterSelectedOptions
                options={customerOptions}
                getOptionLabel={o => o.label}
                value={selectedCustomers}
                onChange={(_, vals) => setSelectedCustomers(vals)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.label}
                        {...tagProps}
                        size="small"
                        className={`${tagProps.className} ${classes.filterChip}`}
                      />
                    );
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {!selectedCustomers.length && (
                            <Chip label="All" size="small" style={{ margin: '2px 4px 2px 0' }} />
                          )}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Loader loaded={!loading}>
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.headerCell}>Customer / FC Overdue</th>
                    <th className={classes.headerCell}>0 days</th>
                    <th className={classes.headerCell}>1 day</th>
                    <th className={classes.headerCell}>1-3 days</th>
                    <th className={classes.headerCell}>4-6 days</th>
                    <th className={classes.headerCell}>7-10 days</th>
                    <th className={classes.headerCell}>11+ days</th>
                  </tr>
                </thead>
                <tbody className={classes.tbody}>
                  {displayData.map((row, index) => (
                    <tr key={row.customer_name}>
                      <td className={classes.cellBold}>{row.customer_name}</td>
                      <td className={classes.cellNumber}>{row.overdue_0_days}</td>
                      <td className={classes.cellNumber}>{row.overdue_1_day}</td>
                      <td className={classes.cellNumber}>{row.overdue_1_3_days}</td>
                      <td className={classes.cellNumber}>{row.overdue_4_6_days}</td>
                      <td className={classes.cellNumber}>{row.overdue_7_10_days}</td>
                      <td className={classes.cellNumber}>{row.overdue_11_plus_days}</td>
                    </tr>
                  ))}
                  {displayData.length > 0 && (
                    <tr
                      className={`${
                        displayData.length % 2 === 0 ? classes.oddRow : classes.evenRow
                      } ${classes.totalRow}`}
                    >
                      <td className={classes.totalCell}>Total</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_0_days}</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_1_day}</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_1_3_days}</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_4_6_days}</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_7_10_days}</td>
                      <td className={classes.totalCellNumber}>{totals.overdue_11_plus_days}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {displayData.length === 0 && !loading && (
                <div className={classes.noData}>
                  <Typography>No overdue FCs found</Typography>
                </div>
              )}
            </div>
          </Loader>
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles as any, { withTheme: true })(OverdueFCsTab);
